import React, { useEffect, useRef } from 'react';

const STATUS_ICONS = { sent: '✓', delivered: '✓✓', read: '✓✓', failed: '✗', pending: '⋯' };
const STATUS_COLORS = { read: 'text-[#0176D3]', failed: 'text-[#BA0517]', pending: 'text-[#C9C9C9]' };

// A generic target="_blank" opens a brand-new tab on every click, even for the
// same URL. Naming the target after the URL itself makes repeat clicks reuse
// (and focus) the tab that's already open instead of stacking new ones.
function externalTarget(url) {
  return `ext_${url.replace(/[^a-zA-Z0-9]/g, '').slice(0, 60)}`;
}

function MessageBubble({ msg }) {
  const isOut = msg.direction === 'outbound';
  const isNote = msg.isNote;

  const bubbleClass = isNote
    ? 'bg-[#FFF4E5] border border-[#FFB75D] text-[#8C4B02] self-center max-w-lg text-xs italic'
    : isOut
    ? 'bg-[#0176D3] text-white self-end max-w-sm'
    : 'bg-white border border-[#E5E5E5] text-[#2E2E2E] self-start max-w-sm';

  const { content = {}, type } = msg;

  return (
    <div className={`flex flex-col mb-3 ${isOut ? 'items-end' : isNote ? 'items-center' : 'items-start'}`}>
      {isNote && <span className="text-xs text-[#DD7A01] mb-1 font-medium">Internal Note</span>}
      <div className={`rounded-lg px-4 py-2 shadow-sm ${bubbleClass}`}>
        {(type === 'text' || type === 'note') && <p className="text-sm leading-relaxed whitespace-pre-wrap">{content.text}</p>}

        {type === 'image' && (
          <div>
            {content.mediaUrl
              ? <img src={content.mediaUrl} alt="img" className="max-w-xs rounded-lg" />
              : <span className="text-xs opacity-70">{content.text || '[Image]'}</span>}
            {content.caption && <p className="text-sm mt-1">{content.caption}</p>}
          </div>
        )}

        {type === 'sticker' && (
          content.mediaUrl
            ? <img src={content.mediaUrl} alt="sticker" className="max-w-[120px]" />
            : <span className="text-2xl">{content.text || '🙂'}</span>
        )}

        {type === 'audio' && (
          content.mediaUrl
            ? <audio controls src={content.mediaUrl} className="max-w-xs" />
            : <span className="text-xs opacity-70">[Audio]</span>
        )}

        {type === 'video' && (
          content.mediaUrl
            ? <video controls src={content.mediaUrl} className="max-w-xs rounded-lg" />
            : <span className="text-xs opacity-70">[Video]</span>
        )}

        {type === 'document' && (
          <a
            href={content.mediaUrl}
            target={externalTarget(content.mediaUrl)}
            rel="noreferrer"
            className="flex items-center gap-2 text-sm underline"
          >
            📎 {content.fileName || 'Document'}
          </a>
        )}

        {type === 'location' && content.latitude && (
          <a
            href={`https://www.google.com/maps?q=${content.latitude},${content.longitude}`}
            target={externalTarget(`${content.latitude},${content.longitude}`)}
            rel="noreferrer"
            className="text-sm underline"
          >
            📍 View Location
          </a>
        )}

        {type === 'template' && (
          <p className="text-sm opacity-80">📋 Template: {content.templateName}</p>
        )}
      </div>

      <div className="flex items-center gap-1 mt-0.5 px-1">
        <span className="text-xs text-[#706E6B]">
          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        {isOut && !isNote && (
          <span className={`text-xs ${STATUS_COLORS[msg.status] || 'text-[#706E6B]'}`}>
            {STATUS_ICONS[msg.status] || ''}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ConversationThread({ messages, loading, loadingMore, hasMoreHistory, onLoadMore }) {
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  useEffect(() => {
    const lastId = messages[messages.length - 1]?._id;
    // Only auto-scroll to the bottom when a NEW message arrived at the end of
    // the thread (initial load or a fresh send/receive) — not when "load
    // earlier history" prepends older messages, which would otherwise yank
    // the view away from whatever the agent was reading.
    if (lastId && lastId !== lastMessageIdRef.current) {
      lastMessageIdRef.current = lastId;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#706E6B]">
        <p className="text-sm">Loading messages…</p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col bg-[#F7F9FB]">
      {hasMoreHistory && (
        <div className="flex justify-center mb-3">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-xs bg-white border border-[#E5E5E5] hover:bg-[#F3F2F2] disabled:opacity-50 text-[#5C5C5C] px-3 py-1.5 rounded shadow-sm whitespace-nowrap"
          >
            {loadingMore ? 'Loading…' : 'Load earlier messages'}
          </button>
        </div>
      )}
      {messages.length === 0 && (
        <p className="text-center text-sm text-[#706E6B] mt-8">No messages yet</p>
      )}
      {messages.map(msg => (
        <MessageBubble key={msg._id || msg.externalMessageId} msg={msg} />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
